import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/features/auth/auth.store';
import { cn } from '@/lib/utils';

/**
 * The client's own logo.
 *
 * `utility/file/read/web/config` is a public file, not an envelope-wrapped API
 * call — the body *is* the config object. Being public is what makes it usable
 * on the sign-in screen, where there is no business and no token yet.
 *
 * Falls back to the signed-in business's own logo, and then to a monogram, so
 * the header never collapses to an empty gap while the file is loading or if a
 * client has not set one.
 */

/**
 * `utility/file/read/web/config` — the client's own settings file.
 *
 * It answers more than the logo: the sidebar gates several pages on flags that
 * live here, which is why the query is shared rather than local to this
 * component. See config/client-flags.ts.
 */
export interface WebConfig {
  adminHeaderLogoCombo?: string | null;
  hasPriceDb?: boolean;
  hasLoyalty?: boolean;
  hasFaq?: boolean;
  /** A price integration feeds this client's prices; item master can re-pull. */
  hasPriceIntegration?: boolean;
}

export function useClientConfig() {
  return useQuery({
    queryKey: ['client-config'],
    queryFn: async () => {
      const response = await api.get<WebConfig>('utility/file/read/web/config');
      return response.data;
    },
    // It is a static file; refetching it per navigation buys nothing.
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
}

export function ClientLogo({
  className,
  fallbackClassName,
}: {
  /** Sizing for the image. Height-constrained: logos are wide, not square. */
  className?: string;
  fallbackClassName?: string;
}) {
  const { t } = useTranslation();
  const { data } = useClientConfig();
  const businessLogo = useAuthStore((s) => s.business?.logoImage);

  const src = data?.adminHeaderLogoCombo || businessLogo;

  if (src) {
    return (
      <img
        src={src}
        alt={t('common.appName')}
        className={cn('w-auto object-contain', className)}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        'bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-lg text-sm font-semibold',
        fallbackClassName
      )}
    >
      {t('common.appName').charAt(0)}
    </span>
  );
}
